import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { Snackbar } from "@mui/material";
import Toast, { ToastTransition } from "./Toast";

/**
 * App-wide toast notifications.
 *
 * Wrap the app once with <NotificationProvider>, then in any component:
 *   const notify = useNotify();
 *   notify.success("Saved");
 *   notify.error(res.message || "Something went wrong");
 *
 * The context value (notify) is referentially stable, so consuming components
 * do not re-render when a toast opens/closes.
 *
 * The look and the motion both come from <Toast>: the book's quiet ground and
 * status dot rather than a saturated fill with an icon glyph, arriving on the
 * 240ms decelerate rise.
 */
const NotificationContext = createContext(null);

export const useNotify = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotify must be used within a <NotificationProvider>");
  }
  return ctx;
};

export function NotificationProvider({ children }) {
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const push = useCallback((message, severity) => {
    setToast({ open: true, message: String(message ?? ""), severity });
  }, []);

  const notify = useMemo(
    () => ({
      success: (message) => push(message, "success"),
      error: (message) => push(message, "error"),
      info: (message) => push(message, "info"),
      warning: (message) => push(message, "warning"),
    }),
    [push]
  );

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={ToastTransition}
      >
        <Toast severity={toast.severity} message={toast.message} />
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
